import { Params, Route } from '@angular/router';
import { MemberArea as IntegrationMemberArea } from '@cassiopeia/integration';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { MemberPolicyState } from '@web/data';
import { FeatureService } from '@web/service';

export type MenuItemState = 'disabled' | 'enabled' | 'hidden' | 'active';
export type MenuItemDivider = 'before' | 'after' | 'none';

interface MenuItemBase {
  icon: IconName;
  label: string;
  notificationId?: string;
  queryParams?: Params;
  priority?: number;
}

interface StateParams {
  hasDisbursements: boolean;
  policyState: Record<MemberPolicyState, boolean>;
}

export interface MenuItemConfig extends MenuItemBase {
  dividerFn?: (member: StateParams, featureService: FeatureService) => MenuItemDivider[];
}

export interface ExtendedRoute extends Route {
  menuItem?: MenuItemConfig;
  children?: ExtendedRoutes;
}

export type ExtendedRoutes = ExtendedRoute[];

export const mergeExtendedRoutes = (route: ExtendedRoute, overrides: Partial<ExtendedRoute>): ExtendedRoute => {
  const menuItem = route.menuItem
    ? {
        ...route.menuItem,
        ...(overrides.menuItem ?? {}),
      }
    : overrides.menuItem;

  const children = route.children?.length ? [...route.children, ...(overrides.children ?? [])] : overrides.children;

  return {
    ...route,
    ...overrides,
    providers: [...(route.providers ?? []), ...(overrides.providers ?? [])],
    children,
    menuItem,
  };
};

export const findArea = (path: string, areas?: IntegrationMemberArea[] | null): IntegrationMemberArea | undefined =>
  areas?.find(({ name }) => name === path);

/** An area the backend defines is shown or hidden by its `hidden` alone; the app's own state decides only the others. */
export const resolveMenuItemState = (
  area: IntegrationMemberArea | undefined,
  appState: MenuItemState | undefined,
): MenuItemState => {
  if (area) return area.hidden ? 'hidden' : 'enabled';
  return appState ?? 'enabled';
};

const AuthenticatedMainRoutes = [
  'root',
  'home',
  'risks',
  'savings',
  'payout',
  'document',
  'selfService',
  'contact',
  'profile',
  'logout',
  'pensionsInfo',
] as const;
export type AuthenticatedMainRoute = (typeof AuthenticatedMainRoutes)[number];
export const AuthenticatedMainRouteArray: AuthenticatedMainRoute[] = [...AuthenticatedMainRoutes];
